import React, { useMemo, useState } from 'react'
import classnames from 'classnames'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import SaberCard from '../components/SaberCard'
import SaberTable, { type SaberTableColumn } from '../components/SaberTable'
import KpiStrip from '../components/KpiStrip'
import StatusBadge from '../components/StatusBadge'
import ChartTooltip from '../components/ChartTooltip'
import { useGetBaseCaseQuery, useGetPortfolioCompletenessQuery, useGetPropertyProgramsQuery } from '../saberApi'
import { ALL_PROPERTIES, useSaberSelection } from '../SaberContext'
import { darkenHex, getSaberChartTheme } from '../theme'
import type { PropertyProgram } from '../saber.types'
import { date, dateTime, integer, kwh, money0, percent } from '../format'
import { performancePctColorClass, CELL_COLOR } from '../cellColor'
import ProFormaTable from './basecase/ProFormaTable'
import ImportDrawer from './basecase/ImportDrawer'
import PropertyPicker from './detail/PropertyPicker'
import { resolvePropertyDisplay } from './detail/labels'
import { useStickyPanelOffset } from '../useStickyPanelOffset'
import { downloadCsv, monthsToCsv } from './basecase/exportCsv'
import { useAppSelector } from '../../../shared/redux/hooks'

// Base Case Model tab (v001 §10) — per-property pro-forma model management.
// TypeAheadSelect property picker → BaseCaseModel dashboard (header chip/badge,
// KPI cards, assumptions + modeled-vs-actual chart, Pro-Forma table) + import
// slide-over and client-side CSV export.

// 404/no-data detection. axiosBaseQuery surfaces the response body as the RTK
// error: Nest's NotFoundException carries statusCode 404; the users/api error
// mapper may also tag entity-not-found.
const isNotFound = (error: unknown): boolean => {
  if (error == null || typeof error !== 'object') return false
  const e = error as { statusCode?: number; status?: number; code?: string }
  return e.statusCode === 404 || e.status === 404 || e.code === 'entity-not-found'
}

/** termMonths → "20 yrs" (1 decimal for partial years). */
const termYearsLabel = (termMonths: number): string => {
  const years = termMonths / 12
  return `${Number.isInteger(years) ? years : years.toFixed(1)} yrs`
}

const SECONDARY_BUTTON =
  'rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700'

const BILLING_ON_PILL =
  'inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300'
const BILLING_OFF_PILL =
  'inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700/40 dark:text-slate-300'

// Programs-for-property table (WEB-6559). Columns mirror the ticket's source
// fields: name + type + capacity, billing-on flag, billing-on date, PTO date.
const programColumns: Array<SaberTableColumn<PropertyProgram>> = [
  {
    key: 'name',
    header: 'Program',
    render: (p) => p.name ?? p.programCuid,
    sortable: true,
    sortValue: (p) => p.name ?? p.programCuid
  },
  { key: 'type', header: 'Type', render: (p) => p.type, sortable: true, sortValue: (p) => p.type },
  { key: 'nem', header: 'NEM', render: (p) => p.nemVersion ?? '—' },
  {
    key: 'capacity',
    header: 'Capacity',
    align: 'right',
    render: (p) => (p.capacityKw != null ? `${integer(p.capacityKw)} kW` : '—'),
    sortable: true,
    sortValue: (p) => p.capacityKw
  },
  {
    key: 'billingOn',
    header: 'Billing',
    render: (p) => <span className={p.billingOn ? BILLING_ON_PILL : BILLING_OFF_PILL}>{p.billingOn ? 'On' : 'Off'}</span>,
    sortable: true,
    sortValue: (p) => (p.billingOn ? 1 : 0)
  },
  {
    key: 'billingOnDate',
    header: 'Billing On',
    align: 'right',
    render: (p) => date(p.billingOnDate),
    sortable: true,
    sortValue: (p) => p.billingOnDate
  },
  { key: 'ptoDate', header: 'PTO', align: 'right', render: (p) => date(p.ptoDate), sortable: true, sortValue: (p) => p.ptoDate }
]

export const BaseCase: React.FC = () => {
  const [importOpen, setImportOpen] = useState(false)
  const { propertyCuid, setPropertyCuid } = useSaberSelection()
  const { permissions } = useAppSelector((state) => state.app)
  const hasUpload = permissions?.includes('base-case-model:upload') ?? false

  // Property options from portfolio completeness (operating properties only;
  // status may be absent on older payloads — keep those rows).
  const { data: portfolio } = useGetPortfolioCompletenessQuery()
  const options = useMemo(
    () =>
      (portfolio?.properties ?? [])
        .filter((p) => p.status == null || p.status === 'OPERATING')
        .map((p) => {
          const { name, location } = resolvePropertyDisplay(p.propertyName, p.location)
          return {
            value: p.propertyCuid,
            label: name,
            sublabel: location ?? undefined,
            detail: p.utility ?? undefined
          }
        }),
    [portfolio]
  )
  const cuid = propertyCuid !== ALL_PROPERTIES ? propertyCuid : options[0]?.value ?? ''
  // Full property row (name/location/utility) for the consolidated header (WEB-6573).
  const selectedProperty = useMemo(
    () => (portfolio?.properties ?? []).find((p) => p.propertyCuid === cuid),
    [portfolio, cuid]
  )

  const { data, isLoading, isError, error, refetch } = useGetBaseCaseQuery(cuid, { skip: cuid === '' })
  const notFound = isError && isNotFound(error)

  // Programs associated with the selected property (WEB-6559). Independent of
  // whether a base-case model has been uploaded, so it renders at the top.
  const { data: programs, isLoading: programsLoading } = useGetPropertyProgramsQuery(cuid, { skip: cuid === '' })

  // Performance to Date = Σ actualRevenue / Σ modeledRevenue × 100 over months
  // with actuals; null (em dash) when there are no actuals.
  const perfToDate = useMemo(() => {
    const withActuals = (data?.months ?? []).filter((m) => m.actualRevenue != null)
    if (withActuals.length === 0) return null
    const modeled = withActuals.reduce((sum, m) => sum + (m.modeledRevenue ?? 0), 0)
    if (modeled === 0) return null
    const actual = withActuals.reduce((sum, m) => sum + (m.actualRevenue ?? 0), 0)
    return (actual / modeled) * 100
  }, [data])

  const year1 = data?.years[0]
  const theme = getSaberChartTheme()
  const chartData = useMemo(
    () =>
      (data?.years ?? []).map((y) => ({
        name: `Y${y.contractYear}`,
        Modeled: y.modeledKwh,
        Actual: y.actualKwh
      })),
    [data]
  )

  const assumptions: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Project', value: data?.project ?? '—' },
    { label: 'Investor', value: data?.investor ?? '—' },
    { label: 'Stage', value: data?.stage ?? '—' },
    { label: 'Term', value: data != null ? `${data.totals.termMonths} months` : '—' },
    { label: 'First month', value: date(data?.totals.firstMonth) },
    { label: 'Last month', value: date(data?.totals.lastMonth) },
    { label: 'Uploaded by', value: data?.uploadedBy ?? '—' },
    { label: 'Uploaded at', value: dateTime(data?.uploadedAt) }
  ]

  const onExport = (): void => {
    if (data == null) return
    downloadCsv(`base-case-${data.propertyCuid}.csv`, monthsToCsv(data.months))
  }

  const { pageHeaderRef, panelStickyProps } = useStickyPanelOffset()

  return (
    <div className="space-y-4 pb-4">
      {/* WEB-6573: consolidated header matching Shadow Billing — title row with
          actions on the right; property (caret opens the picker) · location ·
          utility on the row below, baseline-aligned and color-coded. */}
      <div
        ref={pageHeaderRef}
        className="sticky top-0 z-10 -mx-4 space-y-1 border-b border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Base Case Model</h2>
          <div className="flex flex-wrap items-center gap-2">
            {data != null && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-700/40 dark:text-slate-300">
                Model · {date(data.uploadedAt)}
              </span>
            )}
            {data != null && <StatusBadge status="Uploaded" />}
            {hasUpload && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
              >
                Import
              </button>
            )}
            <button type="button" onClick={onExport} disabled={data == null} className={SECONDARY_BUTTON}>
              Export
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <PropertyPicker
            label={selectedProperty?.propertyName ?? 'Select a property'}
            options={options}
            value={cuid === '' ? null : cuid}
            onSelect={setPropertyCuid}
            suffix={
              selectedProperty?.utility != null ? (
                <>
                  <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
                  <span className="pt-0.5 text-base font-semibold text-indigo-600 dark:text-indigo-400">{selectedProperty.utility}</span>
                </>
              ) : undefined
            }
          />
          {selectedProperty?.location != null && (
            <>
              <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
              <span className={classnames('text-base font-medium', CELL_COLOR.positive)}>
                {selectedProperty.location}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Programs associated with the property (WEB-6559). */}
      {cuid !== '' && (
        <SaberCard title="Programs" {...panelStickyProps(1)}>
          <SaberTable
            columns={programColumns}
            rows={programs ?? []}
            rowKey={(p) => p.programCuid}
            emptyMessage={programsLoading ? 'Loading…' : 'No programs associated with this property.'}
            defaultSort={{ key: 'ptoDate', direction: 'asc' }}
            helpKey="basecasePrograms"
          />
        </SaberCard>
      )}

      {cuid === '' ? (
        <SaberCard>
          <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            Select a property to view its base case model.
          </p>
        </SaberCard>
      ) : isLoading ? (
        <SaberCard>
          <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</p>
        </SaberCard>
      ) : notFound || (!isError && data == null) ? (
        <SaberCard>
          <div className="flex flex-col items-center gap-1 py-8 text-center">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              No base case model uploaded for this property
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Use the Import button to upload a pro-forma CSV or spreadsheet export.
            </p>
          </div>
        </SaberCard>
      ) : isError ? (
        <SaberCard>
          <div className="flex flex-col items-start gap-2 py-6 text-sm text-red-600 dark:text-red-400">
            <span>Failed to load base case model.</span>
            <button
              type="button"
              onClick={() => {
                refetch().catch(() => undefined)
              }}
              className={SECONDARY_BUTTON}
            >
              Retry
            </button>
          </div>
        </SaberCard>
      ) : data != null ? (
        <>
          {/* KPI cards. */}
          <KpiStrip
            items={[
              { label: 'Year-1 Production', value: kwh(year1?.modeledKwh), sublabel: 'Contract year 1 modeled' },
              { label: 'Year-1 Revenue', value: money0(year1?.modeledRevenue), sublabel: 'Gross energy revenue' },
              {
                label: 'Performance to Date',
                value:
                  perfToDate == null ? (
                    '—'
                  ) : (
                    <span className={performancePctColorClass(perfToDate)}>
                      {percent(perfToDate)}
                    </span>
                  ),
                sublabel: 'Σ actual / Σ modeled revenue'
              },
              {
                label: 'Contract Term',
                value: termYearsLabel(data.totals.termMonths),
                sublabel: `from ${date(data.totals.firstMonth)}`
              }
            ]}
          />

          {/* Assumptions | Modeled vs Actual chart. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SaberCard title="Model Assumptions" {...panelStickyProps(2)}>
              <dl className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {assumptions.map((row) => (
                  <div key={row.label} className="flex items-baseline justify-between gap-4 py-1.5">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {row.label}
                    </dt>
                    <dd className="text-right text-slate-900 dark:text-slate-100">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </SaberCard>
            <SaberCard title="Modeled vs Actual Production (kWh)" {...panelStickyProps(3)}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} />
                  <YAxis tick={{ fontSize: 10, fill: theme.axis }} stroke={theme.grid} width={56} />
                  <Tooltip content={<ChartTooltip />} cursor={false} />
                  <Legend wrapperStyle={{ fontSize: 11, color: theme.axis }} />
                  <Bar
                    dataKey="Modeled"
                    fill={theme.series[0]}
                    radius={[3, 3, 0, 0]}
                    activeBar={{ fill: darkenHex(theme.series[0], 0.18) }}
                  />
                  <Bar
                    dataKey="Actual"
                    fill={theme.series[2]}
                    radius={[3, 3, 0, 0]}
                    activeBar={{ fill: darkenHex(theme.series[2], 0.18) }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </SaberCard>
          </div>

          {/* Pro-Forma by Contract Year. */}
          <ProFormaTable years={data.years} months={data.months} totals={data.totals} {...panelStickyProps(4)} />
        </>
      ) : null}

      {importOpen && hasUpload && (
        <ImportDrawer propertyName={data?.propertyName ?? selectedProperty?.propertyName} onClose={() => setImportOpen(false)} />
      )}
    </div>
  )
}

export default BaseCase
